/**
 * Lesson Exercise Generation — fan-out stage using LessonStagePanel.
 * Follows the article pipeline's fan-out pattern (Stage4bSectionDraftingV2).
 */

import React from 'react';
import { SkillTag, skillTagLabel } from '../../../../services/skillFrameworkService';
import { LessonStagePanel } from '../../shared/LessonStagePanel';
import { useLessonContext } from '../../LessonContext';
import { useLessonBatchExecution } from '../../shared/useLessonBatchExecution';
import { EmptyStageNotice } from '../../shared/EmptyStageNotice';
import { useArtifact } from '../../../ContentManagementProject/shared/useArtifact';
import { courseRunsService } from '../../../../services/courseRunsService';

function collectExerciseSpecs(lessonContent: any): Array<{ exercise_id: string; section_title: string; intent: string; context_from_text: string }> {
  const specs: any[] = [];
  for (const section of lessonContent?.sections || []) {
    for (const item of section.content || []) {
      if (item.type === 'exercise') {
        specs.push({
          exercise_id: `ex_${specs.length}`,
          section_title: section.title || '(Opening)',
          intent: item.intent || '',
          context_from_text: item.context_from_text || '',
        });
      }
    }
  }
  return specs;
}

function ExerciseCards({ onSelectedIdChange }: { onSelectedIdChange: (id: string | null) => void }) {
  const { document } = useLessonContext();
  const lessonContent: any = document?.lesson_content;
  const exercises: any = document?.exercises || {};
  const specs = collectExerciseSpecs(lessonContent);

  const {
    selectedId, setSelectedId: setSelectedIdInternal,
    runningAll, batchStatus, runningSingle,
    runSingle, runAll, cancelBatch,
  } = useLessonBatchExecution({ stageId: 'lesson_exercise_gen' });

  const setSelectedId = React.useCallback((id: string | null) => {
    setSelectedIdInternal(id);
    onSelectedIdChange(id);
  }, [setSelectedIdInternal, onSelectedIdChange]);

  const selectedSpec = specs.find((s) => s.exercise_id === selectedId);
  const selectedExercise = selectedId ? exercises[selectedId] : null;

  return (
    <div className="space-y-3">
      {/* Batch controls */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
          disabled={runningAll || specs.length === 0}
          onClick={() => runAll(specs.map((s) => s.exercise_id))}
        >
          {runningAll ? 'Generating All...' : `Generate All (${specs.length})`}
        </button>
        {runningAll && (
          <button type="button" className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={cancelBatch}>Cancel</button>
        )}
        <span className="text-xs text-gray-400">
          {Math.max(
            Object.keys(exercises).length,
            Object.values(batchStatus).filter((s: any) => s.status === 'succeeded').length
          )}/{specs.length} generated
        </span>
      </div>

      {/* Exercise spec cards */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {specs.map((spec) => {
          const hasExercise = !!exercises[spec.exercise_id];
          const batch = batchStatus[spec.exercise_id];
          const ex = exercises[spec.exercise_id];
          return (
            <button
              key={spec.exercise_id}
              type="button"
              className={`flex-shrink-0 border rounded px-3 py-2 text-left text-xs w-52 ${
                selectedId === spec.exercise_id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              } ${hasExercise ? 'bg-green-50' : 'bg-white'}`}
              onClick={() => setSelectedId(spec.exercise_id)}
            >
              <div className="font-medium text-gray-800 truncate">{spec.section_title}</div>
              <div className="text-gray-500 mt-0.5 truncate">{spec.intent.slice(0, 60)}...</div>
              {ex && <div className="text-[10px] mt-1 text-purple-600">{ex.exercise_type}</div>}
              {batch && (
                <div className={`text-[10px] mt-1 ${batch.status === 'succeeded' ? 'text-green-600' : batch.status === 'failed' ? 'text-red-600' : 'text-blue-600'}`}>
                  {batch.status}
                </div>
              )}
              {!batch && hasExercise && !ex && (
                <div className="text-[10px] mt-1 text-green-600">generated</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected exercise detail */}
      {selectedSpec && (
        <div className="border rounded p-3 bg-white space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-gray-800">{selectedSpec.section_title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{selectedSpec.intent}</div>
            </div>
            <button
              type="button"
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={runningSingle || runningAll}
              onClick={() => runSingle(selectedSpec.exercise_id)}
            >
              {runningSingle ? 'Generating...' : selectedExercise ? 'Regenerate' : 'Generate'}
            </button>
          </div>

          {selectedExercise && <ExerciseDisplay exercise={selectedExercise} />}
          {!selectedExercise && <div className="text-xs text-gray-400 italic">Not yet generated.</div>}
        </div>
      )}
    </div>
  );
}

function ExerciseDisplay({ exercise }: { exercise: any }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">{exercise.exercise_type}</span>
        {exercise.skill_category && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{exercise.skill_category}</span>
        )}
        {(exercise.skill_tags || []).map((tag: SkillTag, i: number) => (
          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">{skillTagLabel(tag)}</span>
        ))}
      </div>

      <div className="text-sm font-medium text-gray-900">{exercise.name}</div>
      {exercise.description && <div className="text-xs text-gray-500">{exercise.description}</div>}

      {(exercise.content_blocks || []).map((block: any, i: number) => (
        <div key={i}>
          {block.type === 'text' && (
            <div className="text-sm text-gray-800 bg-gray-50 rounded p-3 whitespace-pre-wrap">{block.value}</div>
          )}
          {block.type === 'answer_list' && (
            <div className="space-y-1">
              {(block.answers || []).map((answer: any, j: number) => (
                <div key={j} className={`border rounded p-2 text-xs ${
                  answer.type === 'correct' ? 'border-green-300 bg-green-50' :
                  answer.type === 'answer_key' ? 'border-blue-300 bg-blue-50' :
                  'border-gray-200'
                }`}>
                  <div className="flex items-start gap-2">
                    <span className={`flex-shrink-0 font-medium ${
                      answer.type === 'correct' ? 'text-green-700' :
                      answer.type === 'answer_key' ? 'text-blue-700' :
                      'text-gray-500'
                    }`}>
                      {answer.type === 'correct' ? '✓' : answer.type === 'answer_key' ? '★' : '○'}
                    </span>
                    <div className="flex-1">
                      <div className="text-gray-800">{answer.text}</div>
                      {answer.feedback && <div className="text-gray-500 mt-1 italic">{answer.feedback}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {exercise.gpt_hint && (
        <details className="text-xs">
          <summary className="text-gray-500 cursor-pointer">AI Grading Hint</summary>
          <div className="mt-1 text-gray-600 bg-gray-50 rounded p-2">{exercise.gpt_hint}</div>
        </details>
      )}
    </div>
  );
}

function renderOutput(doc: Record<string, unknown>) {
  const exercises = (doc.exercises as any) || {};
  const count = Object.keys(exercises).length;
  if (count === 0) return <div className="text-sm text-gray-500">No exercises generated yet.</div>;
  const types = Object.values(exercises).map((e: any) => e.exercise_type);
  const typeCounts: Record<string, number> = {};
  types.forEach((t: string) => { typeCounts[t] = (typeCounts[t] || 0) + 1; });
  return (
    <div className="text-xs text-gray-600">
      {count} exercises generated: {Object.entries(typeCounts).map(([t, c]) => `${c} ${t}`).join(', ')}
    </div>
  );
}

export function LessonExerciseGen() {
  const { document } = useLessonContext();
  const lessonContent = document?.lesson_content as any;
  const hasLessonContent = !!lessonContent?.sections?.length;
  const specs = hasLessonContent ? collectExerciseSpecs(lessonContent) : [];
  const [extractionId, setExtractionId] = React.useState<string | null>(null);

  if (hasLessonContent && specs.length === 0) {
    return <EmptyStageNotice stageId="lesson_exercise_gen" contentType="exercises" />;
  }

  return (
    <LessonStagePanel
      stageId="lesson_exercise_gen"
      isLlmStage
      title="Exercise Generation"
      description="Generate exercises from the specs embedded in the lesson content. Each exercise is generated individually with extended thinking."
      hideRunButton
      renderOutput={renderOutput}
      extractionId={extractionId || undefined}
    >
      {hasLessonContent ? <ExerciseCards onSelectedIdChange={setExtractionId} /> : (
        <div className="text-xs text-gray-500">No lesson content. Generate and review the lesson first.</div>
      )}
    </LessonStagePanel>
  );
}
